import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';

import { DialogCloseReason, DialogService } from '@sui/dialog';

import { LoggerService } from '@services/logger.service';
import { UserObjectsService } from '../../../core/services/user-objects.service';
import { FormsService } from '../../../core/services/forms.service';
import { ObjectEditorService } from 'src/app/core/services/object-editor.service';
import { SortableResult } from 'src/app/core/services/sortable-engine.service';

import { PickerDataobjectsDialogComponent } from 'src/app/core/components/popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

import {
  Widget,
  DataObject,
  ObjectDefinition,
} from '../../../core/models/core.models';
import { PopupType } from '../../../core/constants';
import { NomObjectTypes, NomObjectClass } from '../../../core/constants';

import { editFeedSuccess } from '../../../core/actions/feed.actions';

import { tap, map, take, filter, switchMap, Observable } from 'rxjs';

import cloneDeep from 'lodash-es/cloneDeep';

export interface DataSourceChangeEvent {
  datasource_ids: Array<string>;
  datasource_object_class_ids: Array<number>;
  form_group_property: string;
  form_group_class_id_property: string;
}
@Component({
  selector: 'app-datasource-selector',
  templateUrl: './datasource-selector.component.html',
  styleUrls: ['./datasource-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DatasourceSelectorComponent implements OnInit {
  @Input() formGroup: FormGroup;
  @Input() minCount?: number = 1;
  @Input() hasMultiple?: boolean = false;

  @Input() formGroupProperty: string = 'settings.datasource_object_id';
  @Input() formGroupClassIdProperty: string =
    'settings.datasource_object_class_id';
  @Input() formGroupColorProperty: string = 'settings.datasource_color_id';

  @Input() widget?: Widget;
  @Input() objectTypeIds: Array<number> = [
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
    NomObjectTypes.BROADCAST_FEED,
  ];

  datasources$: Observable<DataObject[]>;

  datasources: Array<DataObject> = [];
  addDatasourceButtonText: string = '';
  noSelectionText: string = '';
  isFeed = false;
  isPivot = false;
  chartSupportsPivots = false;
  shouldShowNotSupportedMessage = false;
  isLoading = false;

  protected destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);

  constructor(
    private actions$: Actions,
    private userObjects: UserObjectsService,
    private formService: FormsService,
    private logger: LoggerService,
    private objectEditorService: ObjectEditorService,
  ) {}

  ngOnInit() {
    this.initializeDataSources();

    this.isFeed = this.formGroupProperty === 'settings.datasource_object_id';
    this.isPivot = this.formGroupProperty === 'settings.dimension_id_pivot';
    this.addDatasourceButtonText = this.isFeed
      ? 'widget-card.label.select-datasource'
      : 'widget-card.label.select-dimension';

    this.noSelectionText = this.isFeed
      ? 'datasource-selector.label.no-datasources'
      : 'datasource-selector.label.no-dimensions';

    this.chartSupportsPivots = this.calculateChartSupportsPivots();
    this.shouldShowNotSupportedMessage =
      this.isPivot && !this.chartSupportsPivots;

    // * listen for Feed Edits event and refresh datasources if one of our datasources was updated
    this.actions$
      .pipe(
        ofType(editFeedSuccess),
        tap(({ feed }) => {
          // Check if the updated feed is one of our current datasources
          const isCurrentDatasource = this.datasources.some(
            (ds) => ds.id === feed.id,
          );

          if (isCurrentDatasource) {
            this.initializeDataSources(true); // Force refresh to bypass cache
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // * Checks if the current chart type supports pivots
  private calculateChartSupportsPivots(): boolean {
    return this.widget?.chart_type?.is_pivotable ?? false;
  }

  // * Initializes the data sources by fetching the objects based on the provided object IDs.
  private initializeDataSources(forceRefresh = false) {
    const datasourceObjectIds = this.formGroup.get([
      this.formGroupProperty,
    ]).value;

    // Check if the value is empty string or empty array
    if (
      !datasourceObjectIds ||
      (Array.isArray(datasourceObjectIds) && datasourceObjectIds.length === 0)
    ) {
      this.logger.debug(
        'No datasourceObjectIds provided. Skipping service call.',
      );

      // Exit the function if the value is empty string or empty array
      return;
    }

    this.isLoading = true;

    this.userObjects
      .objectsByIds$(datasourceObjectIds, forceRefresh)
      .pipe(
        tap((result) => {
          // Clone to avoid mutating the store cache
          this.datasources = cloneDeep(result);

          // Apply form-group colors if present
          const colorValues = this.formGroup.get([
            this.formGroupColorProperty,
          ])?.value;

          const isValidHexColor = (color: string): boolean =>
            typeof color === 'string' &&
            /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color);

          if (Array.isArray(colorValues)) {
            this.datasources.forEach((ds, i) => {
              if (isValidHexColor(colorValues[i])) {
                ds.color = colorValues[i];
              } else {
                ds.color = ds.default_color || null;
                this.logger.debug(
                  `Datasource ${ds.id} color invalid, using default: ${ds.color}`,
                );
              }
            });
          } else {
            this.datasources.forEach((ds) => {
              ds.color = ds.default_color || null;
              this.logger.debug(
                `Datasource ${ds.id} color set to default: ${ds.color}`,
              );
            });
          }

          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  addNew() {
    this.datasources = this.datasources.filter(Boolean);

    const selectedObjectIds = new Map<string, ObjectDefinition>(
      this.datasources.map((ds) => [
        ds.id,
        {
          object_class_id: ds.object_class_id,
          object_type_id: ds.object_type_id,
          object_id: ds.id,
        } as ObjectDefinition,
      ]),
    );

    let dialogRef;

    if (this.isFeed) {
      const maxSelectedCount =
        this.widget?.chart_type?.count_datasource_limit ?? 1;
      const singleSelectionActive = maxSelectedCount === 1;
      const showSelectedOnly = !singleSelectionActive;

      dialogRef = this.dialogService.openTabsDialog(
        PickerDataobjectsDialogComponent,
        {
          data: {
            selectedObjectIds,
            objectTypeIds: this.objectTypeIds,
            singleSelectionActive,
            showSelectedOnly,
            maxSelectedCount,
            popupType: PopupType.SELECT_DATASOURCES,
          },
          width: '61vw',
          minWidth: 1220,
          maxWidth: 1550,
          height: '70vh',
          closeOnClick: false,
          closeOnEscape: true,
        },
      );
    } else {
      const popupType = this.isPivot
        ? PopupType.ATTACH_DIMENSIONS_AS_PIVOTS
        : PopupType.ATTACH_DIMENSIONS_AS_FILTERS;

      dialogRef = this.dialogService.openTabsDialog(
        PickerDataobjectsDialogComponent,
        {
          data: {
            selectedObjectIds,
            objectTypeIds: this.objectTypeIds,
            popupType,
          },
          width: '61vw',
          minWidth: 1220,
          maxWidth: 1550,
          height: '70vh',
          closeOnClick: false,
          closeOnEscape: true,
        },
      );
    }

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event?.data as string[]),
        switchMap((objectIds: string[]) =>
          this.userObjects.objectsByIds$(objectIds),
        ),
        tap((objects: DataObject[]) => {
          this.logger.debug('Selected datasources from dialog:', objects);

          this.datasources = objects.map((object) => ({
            ...object,
            color: object.color || object.default_color || null,
          }));

          this.updateFormGroupValues();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onListUpdated(result: SortableResult) {
    this.logger.debug('listOrderChanged', result);
    this.datasources = result.items;
    this.updateFormGroupValues();
  }

  // * Removes a datasource from the list of datasources.
  removeDatasource(datasource: DataObject) {
    this.datasources = this.datasources.filter((ds) => ds != null); // Filter out null or undefined values
    this.datasources = this.datasources.filter((ds) => ds.id !== datasource.id);
    this.updateFormGroupValues();
  }

  // * with the IDs of the datasources.
  // * Triggers a form change by updating the value of the specified form group property
  updateFormGroupValues() {
    const objectIds = this.datasources
      .filter((datasource) => datasource != null) // Filter out null or undefined values
      .map((datasource) => datasource.id);

    const objectClassIds = this.datasources
      .filter((datasource) => datasource != null)
      .map((datasource) => datasource.object_class_id);

    let colors = this.datasources.map((datasource) => datasource.color);

    // filters should not update colors when selected
    if (this.formGroupProperty === 'settings.dimension_id_filter') {
      colors = colors.map(() => null);
    }

    const updatedValues: { [key: string]: any } = {};

    if (this.formGroupProperty && typeof this.formGroupProperty === 'string') {
      updatedValues[this.formGroupProperty] = objectIds;
    }

    if (
      this.formGroupClassIdProperty &&
      typeof this.formGroupClassIdProperty === 'string'
    ) {
      updatedValues[this.formGroupClassIdProperty] = objectClassIds;
    }

    if (
      this.formGroupColorProperty &&
      typeof this.formGroupColorProperty === 'string'
    ) {
      updatedValues[this.formGroupColorProperty] = colors;
    }

    // Only call populateFormValues if we have at least one valid property
    if (Object.keys(updatedValues).length > 0) {
      this.formService.populateFormValues(this.formGroup, updatedValues);
    }
  }

  replaceDatasource(index: number, newDatasource: DataObject) {
    this.datasources[index] = {
      ...newDatasource,
      color: newDatasource.color || newDatasource.default_color || null,
    };
    this.updateFormGroupValues();
  }

  editDatasource(datasource: DataObject) {
    if (datasource.object_class_id === NomObjectClass.FEED) {
      this.objectEditorService.loadFeedAndOpenPopup(datasource.id);
    }
  }
}
